import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OfflineProvider } from "./context/OfflineContext";
import { SiteLanguageProvider } from "./context/SiteLanguageContext";
import SiteLanguageSelector from "./components/global/SiteLanguageSelector";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const GrievanceForm = lazy(() => import("./pages/GrievanceForm"));
const History = lazy(() => import("./pages/History"));
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminHistory = lazy(() => import("./pages/AdminHistory"));
const SuperAdminLayout = lazy(() => import("./layouts/SuperAdminLayout"));
const ManageAdmins = lazy(() => import("./pages/ManageAdmins"));
const ManageAgents = lazy(() => import("./pages/ManageAgents"));

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isAdmin, isSuperAdmin } = useAuth();
  if (!isAuthenticated) return children;
  if (isSuperAdmin) return <Navigate to="/superadmin/dashboard" replace />;
  if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/dashboard/form" replace />;
}

function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard/form" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { isAuthenticated, isSuperAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/dashboard/form" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-screen">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        </div>
      }
    >
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="form" replace />} />
          <Route path="form" element={<GrievanceForm />} />
          <Route path="history" element={<History />} />
        </Route>
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="form" element={<GrievanceForm />} />
          <Route path="history" element={<AdminHistory />} />
          <Route path="agents" element={<ManageAgents />} />
        </Route>
        <Route
          path="/superadmin"
          element={
            <SuperAdminRoute>
              <SuperAdminLayout />
            </SuperAdminRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="admins" element={<ManageAdmins />} />
          <Route path="agents" element={<ManageAgents />} />
          <Route path="form" element={<GrievanceForm />} />
          <Route path="history" element={<AdminHistory />} />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OfflineProvider>
          <SiteLanguageProvider>
            <div className="min-h-screen">
              <AppRoutes />
            </div>
          </SiteLanguageProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: "10px",
                background: "#1e3a5f",
                color: "#fff",
                fontSize: "14px",
              },
            }}
          />
        </OfflineProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
